import * as React from 'react';
import { BreadCrumb } from '@helpcenter/bread-crumb-dumb';
import { isIncreasedFlexibilityEnabled, isUseHelpCenterRefresh } from '@helpcenter/feature-flags';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import { ElementMargins } from 'view/common/element-root/element-root';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import Breadcrumbs from '@atlaskit/breadcrumbs/breadcrumbs';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { media } from '@atlaskit/primitives/responsive/media';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import type { breadcrumbsElementFragment$key } from './__generated__/breadcrumbsElementFragment.graphql';
import messages from './messages';
export interface BreadcrumbsElementProps {
	breadcrumbsElement?: breadcrumbsElementFragment$key;
	hasTopBarBeenSplit?: boolean;
}

const BreadcrumbsElement: React.FC<BreadcrumbsElementProps> = ({
	breadcrumbsElement,
	hasTopBarBeenSplit,
}) => {
	di(useIsPageInEditMode);
	di(isCSMHelpCenter);
	const { formatMessage } = useIntl();
	const overrideRootBreadcrumbName = isCSMHelpCenter(getEnv().helpCenterType);
	const data = useFragment(
		graphql`
			fragment breadcrumbsElementFragment on HelpLayoutBreadcrumbElement {
				items {
					relativeUrl
					name
				}
			}
		`,
		breadcrumbsElement,
	);
	const breadcrumbs = data?.items || [];
	const [isPageInEditMode] = useIsPageInEditMode();
	const [isPageInPreviewMode] = useIsPageInPreviewMode();

	if (!breadcrumbs || breadcrumbs.length === 0) return null;

	const customStyles = [];
	if (isUseHelpCenterRefresh()) {
		customStyles.push(smallScreenPadding);
	}

	return (
		<ElementRoot
			elementWidth={ElementWidth.COMPACT}
			elementMargins={ElementMargins.COMPACT}
			customStyles={customStyles}
		>
			<Box
				testId="breadcrumb-element-container"
				xcss={[
					breadCrumbsContainer,
					isPageInEditMode && breadCrumbsContainerEditMode,
					!isPageInEditMode &&
						!hasTopBarBeenSplit &&
						isIncreasedFlexibilityEnabled() &&
						breadCrumbsContainerWithTopBarSplit,
				]}
			>
				<Breadcrumbs label={'breadcrumbs'}>
					{breadcrumbs.map((breadcrumb, index: number) => (
						<BreadCrumb
							key={index}
							isFirstCrumb={index === 0}
							to={breadcrumb.relativeUrl}
							name={
								index === 0 && overrideRootBreadcrumbName
									? formatMessage(messages.rootBreadcrumbName)
									: breadcrumb.name
							}
							actionSubjectId="breadcrumbElement"
							tabIndex={isPageInPreviewMode || isPageInEditMode ? -1 : undefined}
						/>
					))}
				</Breadcrumbs>
			</Box>
		</ElementRoot>
	);
};

const breadCrumbsContainer = xcss({
	alignSelf: 'flex-start',
});

const breadCrumbsContainerEditMode = xcss({
	pointerEvents: 'none',
});

const breadCrumbsContainerWithTopBarSplit = xcss({
	paddingLeft: 'space.400',
	paddingRight: 'space.400',
});

const smallScreenPadding = xcss({
	// @ts-ignore
	[media.below.sm]: {
		paddingInline: 'space.400',
	},
});

export default BreadcrumbsElement;
